import { joiResolver } from '@hookform/resolvers/joi'
import LoadingButton from '@mui/lab/LoadingButton'
import { FormControl, FormLabel, Stack, TextField } from '@mui/material'
import { useTranslation } from 'hooks/useTranslation'
import Joi from 'joi'
import ButtonLink from 'modules/button/components/ButtonLink'
import FormAlertError from 'modules/form/components/FormAlertError'
import PasswordInput from 'modules/input/components/PasswordInput'
import { Controller, useForm } from 'react-hook-form'
import { isValidEmail } from 'utils'

export type CredentialsFormValues = { email: string; password: string }

type CredentialsFormProps = {
  busy: boolean
  submitLabel: string
  error?: string
  validateEmailOnSubmit?: boolean
  clearPasswordOnSubmit?: boolean
  onSubmit: (values: CredentialsFormValues) => Promise<void>
  onRecover: (email: string) => void
}

export default function CredentialsForm({
  busy,
  submitLabel,
  error,
  validateEmailOnSubmit = true,
  clearPasswordOnSubmit = false,
  onSubmit,
  onRecover
}: CredentialsFormProps) {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    getValues,
    setError,
    trigger,
    resetField,
    formState: { isSubmitting }
  } = useForm<CredentialsFormValues>({
    defaultValues: { email: '', password: '' },
    resolver: joiResolver(
      Joi.object({
        email: (validateEmailOnSubmit
          ? Joi.string().email({ tlds: { allow: false } })
          : Joi.string()
        ).required(),
        password: Joi.string().required()
      }).messages({
        'string.empty': t('Campo de preenchimento obrigatório'),
        'string.email': t('E-mail inválido')
      })
    )
  })
  const disabled = busy || isSubmitting
  const submit = async (values: CredentialsFormValues) => {
    if (busy) return
    if (clearPasswordOnSubmit) resetField('password')
    await onSubmit(values)
  }
  const recover = async () => {
    if (disabled) return
    const email = getValues('email')
    if (validateEmailOnSubmit) {
      if (!(await trigger('email'))) return
    } else if (!email || !isValidEmail(email)) {
      setError('email', {
        type: 'validate',
        message: t(email ? 'E-mail inválido' : 'Campo de preenchimento obrigatório')
      })
      return
    }
    if (clearPasswordOnSubmit) resetField('password')
    onRecover(email)
  }

  return (
    <Stack
      gap="20px"
      sx={{ 'input:-webkit-autofill': { WebkitBoxShadow: '0 0 0 30px white inset' } }}
    >
      {error ? <FormAlertError message={error} /> : null}
      <form onSubmit={handleSubmit(submit)} noValidate>
        <Stack gap="30px">
          <Controller
            control={control}
            name="email"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <FormControl required>
                <FormLabel htmlFor="login-email">{t('E-mail')}</FormLabel>
                <TextField
                  {...field}
                  inputRef={ref}
                  id="login-email"
                  type={validateEmailOnSubmit ? 'email' : 'text'}
                  autoComplete="username"
                  disabled={disabled}
                  placeholder={t('Digite seu e-mail')}
                  error={!!error}
                  helperText={error?.message}
                />
              </FormControl>
            )}
          />
          <Stack gap="10px" alignItems="flex-start">
            <Controller
              control={control}
              name="password"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <FormControl required>
                  <FormLabel htmlFor="login-password">{t('Senha')}</FormLabel>
                  <PasswordInput
                    {...field}
                    inputRef={ref}
                    id="login-password"
                    autoComplete="current-password"
                    disabled={disabled}
                    placeholder={t('Digite sua senha')}
                    error={!!error}
                    helperText={error?.message}
                  />
                </FormControl>
              )}
            />
            <ButtonLink disabled={disabled} onClick={() => void recover()}>
              {t('Esqueceu sua senha?')}
            </ButtonLink>
          </Stack>
          <LoadingButton
            variant="contained"
            size="large"
            type="submit"
            loading={disabled}
            disabled={disabled}
            sx={{ marginTop: '10px' }}
          >
            {submitLabel}
          </LoadingButton>
        </Stack>
      </form>
    </Stack>
  )
}
